import { PayloadAction } from '@reduxjs/toolkit'
import { call, takeLatest, put } from 'redux-saga/effects'
import axios from 'shared/utils/axios'
import { exportProgram, exportEvent, exportAllProgram } from './reducer'
import salaryOptions from 'shared/constants/salaryOptions'
import { RACE_SELF_DESCRIBE_OPTION, OTHER_GENDER_OPTION } from 'shared/constants/user'
import { exportJsonToCsv } from 'shared/utils/tools'
import queryString from 'qs'
import { showSuccess, hideAllDialogs, showError } from '../app/reducer'
import { PaginatedList, ReportResponse } from 'shared/types'
import { ParticipantExportType } from 'pages/Participants/model/participant'
import range from 'lodash/range'
import flatMap from 'lodash/flatMap'
import entries from 'lodash/entries'
import day from 'dayjs'

function* ExportProgram(action: PayloadAction<{ id: string; name: string }>) {
  try {
    const report: ReportResponse = yield call(axios.request, {
      url: `/program/${action.payload.id}/report`,
    })
    const surveyKeys = report.data.length ? Object.keys(report.data[0]).filter(e => e.includes('Completion')) : []
    const sessionKeys = report.data.length ? Object.keys(report.data[0]).filter(e => e.includes('Attended')) : []

    if (report.data.length === 0) {
      yield put(
        showError({
          message: 'No Records Found',
          subText: `There are no participants that are registered for this program.`,
          showSubText: true,
        }),
      )
      return false
    }

    const data = report.data?.map((each: any) => {
      let race = each.race
      if (each.race) {
        if (typeof each.race === 'string') {
          race = RACE_SELF_DESCRIBE_OPTION
          if (each.race !== RACE_SELF_DESCRIBE_OPTION) {
            race = each.race
          }
        } else {
          if (each.race.includes(RACE_SELF_DESCRIBE_OPTION)) {
            race = each.specific_race
          } else {
            race = each.race.join(' ')
          }
        }
      }

      let salary_range_arr = salaryOptions.filter(salary_option => {
        return salary_option.value === each.household_income
      })

      let salary_range_label = each.household_income
      if (salary_range_arr.length) salary_range_label = salary_range_arr[0].label

      return {
        'Program ID': each.program_id || 'N/A',
        'Member ID': each.member_id || 'N/A',
        Username: each.username || 'N/A',
        'First Name': each.first_name || 'N/A',
        'Last Name': each.last_name || 'N/A',
        Email: each.email || 'N/A',
        'Contact Name': each.contact_name || 'N/A',
        'Phone Number': each.phone_number || 'N/A',
        Gender: each.gender ? (each.gender.includes(OTHER_GENDER_OPTION) ? each.specific_gender : each.gender) : 'N/A',
        Income: salary_range_label || 'N/A',
        Race: race || 'N/A',
        'Medical Consideration': each.medical_conditions.join(' ') || 'N/A',
        Points: each.points || 'N/A',
        FSA: each.fsa || 'N/A',
        Age: each.age || 'N/A',
        'Program Name': each.program_name || 'N/A',
        Sessions: each.sessions || 'N/A',
        'Session Attendance Percentage': each.sessions_attended_percentage || 'N/A',
        'Session Attendance Count': each.sessions_attended_count || 'N/A',
        ...each,
      }
    })

    const fields = [
      'Program ID',
      'Member ID',
      'Username',
      'First Name',
      'Last Name',
      'Email',
      'Contact Name',
      'Phone Number',
      'Gender',
      'Income',
      'Race',
      'Medical Consideration',
      'Points',
      'FSA',
      'Age',
      'Program Name',
      'Sessions',
      'Session Attendance Percentage',
      'Session Attendance Count',
      ...sessionKeys,
      ...surveyKeys,
    ]

    yield exportJsonToCsv(data, fields, report.filename)
    put(showSuccess({ message: 'Program successfully exported' }))
  } catch (err) {
    console.error(err)
  }
}

function* ExportAllProgram(action: PayloadAction<ParticipantExportType>) {
  type ProgramRowItem = {
    name: string
    genders: string[]
    age_category: string
    week_day: string
    activity_type: string
    category_type: string
    start_date: null | string[]
    total_hours: null | number
    mean_age: null | number
    members_limit: null | number[]
    total_registrant_count: null | number
    total_attendees_count: null | number
    total_absent_count: null | number
    avg_attendees_count: null | number
    avg_total_participant: null | number
  }

  try {
    const size = 1000
    const first: PaginatedList<ProgramRowItem> = yield call(axios.request, {
      url: `/base/program_report?${queryString.stringify({ page: 0, size })}`,
    })

    const total = first.count
    let batches = Math.ceil(total / size) - 1
    if (batches < 0) batches = 0

    const fields = [
      'Program Name',
      'Genders',
      'Age Category',
      'Mean Age',
      'Day of Week',
      'Program Activity',
      'Program Type',
      'Program Capacity',
      'Program Start Time',
      'Program Total Hours',
      'Total Registered',
      'Total Present',
      'Total Absent',
      'Total Attendance',
      'Average Session Attendance',
      'Average Participant per Session',
    ]

    const promises = yield (async function request(page, index, results: any[]): Promise<any> {
      try {
        if (page[index] === undefined) return results

        const response = await axios.request({
          url: `/base/program_report?${queryString.stringify({ page: index + 1, size })}`,
        })

        return request(page, index + 1, [...results, response])
      } catch (err) {
        return results
      }
    })(range(0, batches), 0, [first])

    const results = flatMap(promises, ({ data }) =>
      data.map((each: any) => ({
        'Program Name': each.name || 'N/A',
        Genders: each.genders.join('\n'),
        'Age Category': each.age_category || 'N/A',
        'Mean Age': each.mean_age || 'N/A',
        'Day of Week': each.week_day || 'N/A',
        'Program Activity': each.activity_type || 'N/A',
        'Program Capacity': !each.start_date || !each?.start_date.length ? 'N/A' : each?.members_limit[0],
        'Program Type': each.category_type || 'N/A',
        'Program Start Time':
          !each.start_date || !each?.start_date.length ? 'N/A' : day(each?.start_date[0]).format('h:mm A'),
        'Program Total Hours': each.total_hours || 'N/A',
        'Total Registered': each.total_registrant_count || 'N/A',
        'Total Present': each.total_attendees_count || 'N/A',
        'Total Absent': each.total_absent_count || 'N/A',
        'Total Attendance ': each.total_attendees_count || 'N/A',
        'Average Session Attendance': each.avg_attendees_count || 'N/A',
        'Average Participant per Session': each.avg_total_participant || 'N/A',
      })),
    )

    yield exportJsonToCsv(results, fields, 'Program Attendance Summary')
    yield put(hideAllDialogs())
    yield put(showSuccess({ message: 'Participants Successfully Exported' }))
  } catch (err) {
    console.error(err)
  }
}

function* ExportEvent(action: PayloadAction<{ id: string; name: string }>) {
  type EventReponse = {
    name: string
    start_date: string
    end_date: string
    attendees: String[]
  }

  type Response = {
    event: EventReponse
    data: object[]
  }

  try {
    const report: Response = yield call(axios.request, {
      url: `/appointment/event/${action.payload.id}/report`,
    })

    const fields = [
      'Member ID',
      'Username',
      'First name',
      'Last name',
      'Age',
      'Gender',
      'Email',
      'Phone',
      'Postal Code',
      'Forward Sortation Area',
      'Points',
      'Event Name',
      'Event Date',
      'Event Attended',
    ]

    const surveyKeys = report.data.length ? Object.keys(report.data[0]).filter(e => e.includes('Completion')) : []

    const formatEvent = report.data.map((each: any) => {
      const formattedSurveys = entries(each).reduce((keys, [key, value]) => {
        if (!key.includes('Completion')) return keys
        return { ...keys, [key]: value ? 'YES' : 'NO' }
      }, {})

      return {
        ...each,
        'Member ID': each.member_id,
        Username: each.name || 'N/A',
        'First name': each.first_name || 'N/A',
        'Last name': each.last_name || 'N/A',
        Age: each.age || 'N/A',
        Gender: each.gender || 'N/A',
        Email: each.email || 'N/A',
        Phone: each.phone_number || 'N/A',
        'Postal Code': each.postal_code || 'N/A',
        'Forward Sortation Area': each?.postal_code?.substring(0, 3) || 'N/A',
        Points: each.points || 'N/A',
        'Event Name': report.event.name || 'N/A',
        'Event Date': report?.event?.start_date ? day(report?.event?.start_date).format('MM/DD/YYYY') : 'N/A',
        'Event Attended': report.event.attendees.includes(each.id) ? 'YES' : 'NO',
        ...formattedSurveys,
      }
    })

    yield exportJsonToCsv(
      formatEvent,
      [...fields, ...surveyKeys],
      `${report.event.name} : ${report.event.start_date} - ${report.event.end_date}`,
    )
    yield put(showSuccess({ message: 'Event successfully exported' }))
  } catch (err) {
    console.error(err)
  }
}

export default function* rootSaga() {
  yield takeLatest(exportAllProgram.type, ExportAllProgram)
  yield takeLatest(exportProgram.type, ExportProgram)
  yield takeLatest(exportEvent.type, ExportEvent)
}
